import { ErrorMessage } from "@/components/ErrorMessage";
import { Button } from "@/components/flexkit/Button";
import {
    useCreateWorkflow,
    useGetWorkflow,
    useUpdateWorkflow,
    useCanDeleteAutomation,
    type PlainWorkflow,
} from "@/queries/automation-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import {
    DEFAULT_WORKFLOW_LIMITS,
    WorkflowDetailsTab,
    workflowDetailsUrl,
    workflowName,
    workflowsUrl,
} from "@/utils/automations";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { useParams, useNavigate, useBlocker } from "react-router-dom";
import { AutomationDefinitionSectionEdit } from "@/routes/automations/automation-edit-page/AutomationDefinitionSectionEdit";
import { atom, useAtom, useAtomValue, useStore } from "jotai";
import { atomEffect } from "jotai-effect";
import {
    convertWorkflowToFormData,
    type WorkflowEditFormData,
    type WorkflowEditFormStepData,
    type WorkflowEditFormStepType,
    type WorkflowEditFormTriggerData,
    type WorkflowEditFormLimitsData,
    areStepsEqual,
    areTriggersEqual,
    areExecutorsEqual,
    areLimitsEqual,
    convertFormTriggersToProtobuf,
    convertFormStepsToWorkflowStep,
} from "@/components/automations/automation-edit-form-data";
import { newWorkflowFromTemplate, type AutomationTemplate } from "@/utils/automation-templates";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatWorkflowError } from "@/utils/errors";
import { v4 as uuidv4 } from "uuid";
import type { SidePanelEditingState } from "@/routes/automations/automation-edit-page/types";
import { useDocumentTitle } from "@/hooks/use-document-title";
import type { PlainMessage } from "@bufbuild/protobuf";
import { type Subject } from "gitpod-next-api/gitpod/v1/identity_pb";
import { UnsavedChangesDialog } from "@/routes/automations/automation-edit-page/UnsavedChangesDialog";
import { WorkflowAction, WorkflowAction_Limits } from "gitpod-next-api/gitpod/v1/workflow_pb";
import { SubPageHeader } from "@/components/flexkit/page/SubPageHeader";
import { Page } from "@/components/flexkit/page/Page";
import { PageContent } from "@/components/flexkit/page/PageContent";
import { DropdownActions } from "@/components/podkit/dropdown/DropDownActions";
import { DropdownMenuItem, DropdownMenuSeparator } from "@/components/podkit/dropdown/DropDown";
import { IconEllipsisHorizontal } from "@/assets/icons/geist/IconEllipsisHorizontal";
import { RenameAutomationDialog } from "@/components/automations/RenameAutomationDialog";
import { DeleteWorkflowDialog } from "@/components/automations/DeleteAutomationDialog";
import { withGitpodSize } from "@/assets/icons/utils";
import IconFloppyDisk1Base from "central-icons/IconFloppyDisk1";
import IconPencilBase from "central-icons/IconPencil";
import IconTrashCanBase from "central-icons/IconTrashCan";

const IconFloppyDisk = withGitpodSize(IconFloppyDisk1Base);
const IconPencil = withGitpodSize(IconPencilBase);
const IconTrashCan = withGitpodSize(IconTrashCanBase);

/**
 * Creates all atoms needed for the edit page.
 * These atoms are scoped to this component instance.
 */
function useEditPageAtoms() {
    return useMemo(() => {
        // Core state atoms
        const currentWorkflowAtom = atom<PlainWorkflow | undefined>(undefined);
        const editableStepsAtom = atom<WorkflowEditFormStepData[] | null>(null);
        const editableTriggersAtom = atom<WorkflowEditFormTriggerData[]>([]);
        const editableExecutorAtom = atom<PlainMessage<Subject> | undefined>(undefined);
        const editableLimitsAtom = atom<WorkflowEditFormLimitsData | undefined>(undefined);
        const sidePanelEditingAtom = atom<SidePanelEditingState>(null);
        const newStepAtom = atom<WorkflowEditFormStepData | null>(null);

        // Initial state for dirty tracking
        const initialFormDataAtom = atom<WorkflowEditFormData | null>(null);

        const isDirtyDisabled = atom(false);
        // Derived atom: is the form dirty (has unsaved changes)
        const isDirtyAtom = atom((get) => {
            const initial = get(initialFormDataAtom);
            if (!initial) {
                // No initial state yet, not dirty
                return false;
            }

            if (get(isDirtyDisabled)) {
                // The automation was just created/updated, so it's not dirty anymore.
                return false;
            }

            const currentSteps = get(editableStepsAtom);
            const currentTriggers = get(editableTriggersAtom);
            const currentExecutor = get(editableExecutorAtom);
            const currentLimits = get(editableLimitsAtom);

            return (
                !areStepsEqual(currentSteps, initial.steps) ||
                !areTriggersEqual(currentTriggers, initial.triggers) ||
                !areExecutorsEqual(currentExecutor, initial.executor) ||
                !areLimitsEqual(currentLimits, initial.limits)
            );
        });

        // Derived atom: is a new step being created (vs editing existing)
        const isCreatingStepAtom = atom((get) => {
            const editing = get(sidePanelEditingAtom);
            return editing === null && get(newStepAtom) !== null;
        });

        // Derived atom: the step currently being edited (from editableStepsAtom or newStepAtom)
        const currentEditingStepAtom = atom((get) => {
            const editing = get(sidePanelEditingAtom);
            if (editing?.type === "step") {
                return get(editableStepsAtom)?.[editing.index] ?? null;
            }
            return get(newStepAtom);
        });

        // Action atom: replace step at index
        const replaceStepAtom = atom(
            null,
            (get, set, { index, step }: { index: number; step: WorkflowEditFormStepData }) => {
                const steps = get(editableStepsAtom);
                if (steps) {
                    set(editableStepsAtom, [...steps.slice(0, index), step, ...steps.slice(index + 1)]);
                }
            },
        );

        // Action atom: remove step at index
        const removeStepAtom = atom(null, (get, set, index: number) => {
            const steps = get(editableStepsAtom);
            if (steps) {
                set(
                    editableStepsAtom,
                    steps.filter((_, i) => i !== index),
                );
                const editing = get(sidePanelEditingAtom);
                if (editing?.type === "step") {
                    if (index < editing.index) {
                        set(sidePanelEditingAtom, { type: "step", index: editing.index - 1 });
                    } else if (index === editing.index) {
                        set(sidePanelEditingAtom, null);
                    }
                }
            }
        });

        // Action atom: create new step
        const createNewStepAtom = atom(null, (_get, set, type: WorkflowEditFormStepType = "command") => {
            set(sidePanelEditingAtom, null);
            switch (type) {
                case "prompt":
                case "command":
                    set(newStepAtom, {
                        id: uuidv4(),
                        type,
                        content: "",
                    });
                    break;
                case "pr":
                    set(newStepAtom, {
                        id: uuidv4(),
                        type,
                        title: "",
                        description: "",
                        branch: "",
                        isDraft: false,
                    });
                    break;
                case "report":
                    set(newStepAtom, {
                        id: uuidv4(),
                        type,
                        outputs: [],
                    });
                    break;
            }
        });

        // Effect: create step if empty
        const createStepIfEmptyAtomEffect = atomEffect((get, set) => {
            const steps = get(editableStepsAtom);
            if (steps && steps.length === 0) {
                set(createNewStepAtom, "command");
            }
        });

        // Track if initial sync has happened
        const initialSyncDoneAtom = atom(false);

        // Effect: sync workflow to editable atoms
        const syncWorkflowToEditableAtomsEffect = atomEffect((get, set) => {
            const workflow = get(currentWorkflowAtom);
            if (!workflow) {
                return;
            }

            // Only sync once on initial load
            const syncDone = get(initialSyncDoneAtom);
            if (syncDone) {
                return;
            }

            const formData = convertWorkflowToFormData(workflow);

            // Sync editable atoms from form data
            set(editableStepsAtom, formData.steps);
            set(editableTriggersAtom, formData.triggers);
            set(editableExecutorAtom, formData.executor);
            set(editableLimitsAtom, formData.limits);

            // Store initial state for dirty tracking
            set(initialFormDataAtom, formData);

            set(initialSyncDoneAtom, true);
        });

        // Action atom: update the current step (either in editableStepsAtom or newStepAtom)
        const updateCurrentStepAtom = atom(null, (get, set, updates: Partial<WorkflowEditFormStepData>) => {
            const editing = get(sidePanelEditingAtom);
            if (editing?.type === "step") {
                // Editing existing step - update in editableStepsAtom
                const steps = get(editableStepsAtom);
                if (steps) {
                    const currentStep = steps[editing.index];
                    const updatedStep = { ...currentStep, ...updates } as WorkflowEditFormStepData;
                    set(replaceStepAtom, { index: editing.index, step: updatedStep });
                }
            } else {
                // Creating new step - update newStepAtom
                const newStep = get(newStepAtom);
                if (newStep) {
                    set(newStepAtom, { ...newStep, ...updates } as WorkflowEditFormStepData);
                }
            }
        });

        // Action atom: add the new step to editableStepsAtom and start creating another
        const addNewStepAtom = atom(null, (get, set) => {
            const newStep = get(newStepAtom);
            if (!newStep) {
                return;
            }

            const steps = get(editableStepsAtom);
            set(editableStepsAtom, steps ? [...steps, newStep] : [newStep]);
            set(createNewStepAtom, "command");
        });

        return {
            currentWorkflowAtom,
            editableStepsAtom,
            editableTriggersAtom,
            editableExecutorAtom,
            editableLimitsAtom,
            sidePanelEditingAtom,
            newStepAtom,
            currentEditingStepAtom,
            isCreatingStepAtom,
            initialFormDataAtom,
            isDirtyAtom,
            isDirtyDisabled,
            removeStepAtom,
            createNewStepAtom,
            createStepIfEmptyAtomEffect,
            updateCurrentStepAtom,
            addNewStepAtom,
            syncWorkflowToEditableAtomsEffect,
        };
    }, []);
}

type AutomationEditPageProps = {
    template?: AutomationTemplate;
};

export const AutomationEditPage: FC<AutomationEditPageProps> = ({ template }) => {
    const navigate = useNavigate();
    const { toast } = useToast();

    const { workflowId } = useParams<{ workflowId: string }>();
    const { data: workflowData, error: workflowError } = useGetWorkflow(template ? undefined : workflowId);
    const { mutateAsync: updateWorkflow, isPending: isUpdatingWorkflow } = useUpdateWorkflow();
    const { mutateAsync: createWorkflow, isPending: isCreatingWorkflow } = useCreateWorkflow();
    const { data: currentUser } = useAuthenticatedUser();
    const { canDeleteAutomation } = useCanDeleteAutomation(workflowId);

    const [showRenameDialog, setShowRenameDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    const atoms = useEditPageAtoms();
    const store = useStore();
    const [workflow, setWorkflow] = useAtom(atoms.currentWorkflowAtom);
    const isDirty = useAtomValue(atoms.isDirtyAtom);

    // Set document title based on whether we're editing an existing workflow or creating a new one
    const documentTitle = template ? undefined : workflow ? `Edit ${workflowName(workflow)}` : undefined;
    useDocumentTitle(documentTitle);

    // Mount atom effect to sync workflow to editable atoms
    useAtom(atoms.syncWorkflowToEditableAtomsEffect);

    // Block navigation when there are unsaved changes
    const blocker = useBlocker(() => {
        return store.get(atoms.isDirtyAtom);
    });

    useEffect(() => {
        if (template && currentUser) {
            setWorkflow(newWorkflowFromTemplate(template, currentUser?.id));
        } else if (workflowId) {
            setWorkflow(workflowData);
        }
    }, [workflowData, template, workflowId, setWorkflow, currentUser]);

    const handleCancel = useCallback(() => {
        if (template) {
            navigate(workflowsUrl());
        } else if (workflowId) {
            // Go back to preserve the tab that was selected, fall back to details URL if no history
            if (window.history.state && window.history.state.idx > 0) {
                navigate(-1);
            } else {
                navigate(workflowDetailsUrl({ id: workflowId }));
            }
        }
    }, [navigate, template, workflowId]);

    const handleCreate = useCallback(async () => {
        if (!workflow) return;

        const steps = store.get(atoms.editableStepsAtom);
        const triggers = store.get(atoms.editableTriggersAtom);
        const executor = store.get(atoms.editableExecutorAtom);
        const limits = store.get(atoms.editableLimitsAtom);
        try {
            const createdWorkflow = await createWorkflow({
                name: workflow.metadata?.name || "",
                description: workflow.metadata?.description || "",
                executor: executor,
                triggers: convertFormTriggersToProtobuf(triggers),
                steps: convertFormStepsToWorkflowStep(steps || []),
                limits: new WorkflowAction_Limits({
                    maxParallel: limits?.maxParallel ?? DEFAULT_WORKFLOW_LIMITS.maxParallel,
                    maxTotal: limits?.maxTotal ?? DEFAULT_WORKFLOW_LIMITS.maxTotal,
                }),
            });
            // Mark form as clean before navigating to prevent unsaved changes dialog
            store.set(atoms.isDirtyDisabled, true);
            navigate(workflowDetailsUrl({ id: createdWorkflow.id, tab: WorkflowDetailsTab.Definition }), {
                replace: true,
            });
        } catch (error) {
            toast({
                title: "Failed to create automation",
                description: formatWorkflowError(error),
            });
        }
    }, [
        atoms.editableExecutorAtom,
        atoms.editableLimitsAtom,
        atoms.editableStepsAtom,
        atoms.editableTriggersAtom,
        atoms.isDirtyDisabled,
        createWorkflow,
        navigate,
        store,
        toast,
        workflow,
    ]);

    const handleSave = useCallback(
        async (workflowId: string) => {
            const steps = store.get(atoms.editableStepsAtom);
            const triggers = store.get(atoms.editableTriggersAtom);
            const executor = store.get(atoms.editableExecutorAtom);
            const limits = store.get(atoms.editableLimitsAtom);

            if (!steps) {
                return;
            }

            try {
                await updateWorkflow({
                    workflowId,
                    executor,
                    triggers: convertFormTriggersToProtobuf(triggers),
                    action: new WorkflowAction({
                        limits: new WorkflowAction_Limits({
                            maxParallel: limits?.maxParallel ?? DEFAULT_WORKFLOW_LIMITS.maxParallel,
                            maxTotal: limits?.maxTotal ?? DEFAULT_WORKFLOW_LIMITS.maxTotal,
                        }),
                        steps: convertFormStepsToWorkflowStep(steps),
                    }),
                });
                store.set(atoms.isDirtyDisabled, true);
                navigate(workflowDetailsUrl({ id: workflowId, tab: WorkflowDetailsTab.Definition }));
            } catch (error) {
                toast({
                    title: "Failed to save changes to the automation",
                    description: formatWorkflowError(error),
                });
            }
        },
        [
            atoms.editableExecutorAtom,
            atoms.editableLimitsAtom,
            atoms.editableStepsAtom,
            atoms.editableTriggersAtom,
            atoms.isDirtyDisabled,
            navigate,
            store,
            toast,
            updateWorkflow,
        ],
    );

    const handleBlockerConfirmLeave = useCallback(() => {
        if (blocker.state === "blocked") {
            blocker.proceed();
        }
    }, [blocker]);

    const handleBlockerStay = useCallback(() => {
        if (blocker.state === "blocked") {
            blocker.reset();
        }
    }, [blocker]);

    const handleRenamed = useCallback(
        (name: string, description: string) => {
            if (workflow) {
                setWorkflow({
                    ...workflow,
                    metadata: {
                        ...workflow.metadata,
                        name,
                        description,
                    },
                });
            }
        },
        [workflow, setWorkflow],
    );

    // Build breadcrumbs based on whether we're creating or editing
    const breadcrumbs = useMemo(() => {
        if (template && workflow) {
            // Creating new automation from template
            return [{ label: "Automations", to: workflowsUrl() }, { label: workflowName(workflow) }];
        }
        if (workflow) {
            return [
                { label: "Automations", to: workflowsUrl() },
                { label: workflowName(workflow), to: workflowDetailsUrl({ id: workflow.id }) },
                { label: "Edit" },
            ];
        }
        return [];
    }, [template, workflow]);

    return (
        <Page data-testid="workflow-edit-page" className="size-full">
            <SubPageHeader
                breadcrumbs={breadcrumbs}
                loading={!workflow && !template ? { breadcrumbItemCount: 3 } : undefined}
                actions={
                    <AutomationEditActions
                        workflow={workflow}
                        isTemplate={!!template}
                        isDirty={isDirty}
                        isCreatingWorkflow={isCreatingWorkflow}
                        isUpdatingWorkflow={isUpdatingWorkflow}
                        canDeleteAutomation={canDeleteAutomation}
                        onCancel={handleCancel}
                        onCreate={handleCreate}
                        onSave={handleSave}
                        onRename={() => setShowRenameDialog(true)}
                        onDelete={() => setShowDeleteDialog(true)}
                    />
                }
            />
            <PageContent padding="none" scrollable={false}>
                <ErrorMessage error={workflowError} className="mx-6 mt-6" />

                {workflow && (
                    <AutomationDefinitionSectionEdit
                        workflow={workflow}
                        editableStepsAtom={atoms.editableStepsAtom}
                        editableTriggersAtom={atoms.editableTriggersAtom}
                        editableExecutorAtom={atoms.editableExecutorAtom}
                        editableLimitsAtom={atoms.editableLimitsAtom}
                        sidePanelEditingAtom={atoms.sidePanelEditingAtom}
                        newStepAtom={atoms.newStepAtom}
                        currentEditingStepAtom={atoms.currentEditingStepAtom}
                        isCreatingStepAtom={atoms.isCreatingStepAtom}
                        removeStepAtom={atoms.removeStepAtom}
                        createNewStepAtom={atoms.createNewStepAtom}
                        createStepIfEmptyAtomEffect={atoms.createStepIfEmptyAtomEffect}
                        updateCurrentStepAtom={atoms.updateCurrentStepAtom}
                        addNewStepAtom={atoms.addNewStepAtom}
                    />
                )}
            </PageContent>

            <UnsavedChangesDialog
                open={blocker.state === "blocked"}
                onOpenChange={handleBlockerStay}
                onConfirmLeave={handleBlockerConfirmLeave}
            />

            {workflow && (
                <>
                    <RenameAutomationDialog
                        workflow={workflow}
                        open={showRenameDialog}
                        onClose={() => setShowRenameDialog(false)}
                        onRenamed={handleRenamed}
                        isTemplate={!!template}
                    />
                    <DeleteWorkflowDialog
                        workflow={workflow}
                        open={showDeleteDialog}
                        onClose={() => setShowDeleteDialog(false)}
                    />
                </>
            )}
        </Page>
    );
};

type AutomationEditActionsProps = {
    workflow: PlainWorkflow | undefined;
    isTemplate: boolean;
    isDirty: boolean;
    isCreatingWorkflow: boolean;
    isUpdatingWorkflow: boolean;
    canDeleteAutomation: boolean;
    onCancel: () => void;
    onCreate: () => void;
    onSave: (workflowId: string) => void;
    onRename: () => void;
    onDelete: () => void;
};

const AutomationEditActions: FC<AutomationEditActionsProps> = ({
    workflow,
    isTemplate,
    isDirty,
    isCreatingWorkflow,
    isUpdatingWorkflow,
    canDeleteAutomation,
    onCancel,
    onCreate,
    onSave,
    onRename,
    onDelete,
}) => {
    return (
        <div className="flex items-center gap-2">
            {/* Actions menu - show for both new and existing automations */}
            {workflow && (
                <DropdownActions
                    triggerTestId="automation-edit-actions-dropdown-trigger"
                    button={<Button variant="ghost" aria-label="More actions" LeadingIcon={IconEllipsisHorizontal} />}
                    contentClassName="w-48"
                >
                    <DropdownMenuItem
                        onClick={onRename}
                        data-testid="automation-edit-actions-rename"
                        data-tracking-id="rename-automation-edit-actions"
                        LeadingIcon={IconPencil}
                    >
                        Rename
                    </DropdownMenuItem>
                    {!isTemplate && canDeleteAutomation && (
                        <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                variant="destructive"
                                onClick={onDelete}
                                data-testid="automation-edit-actions-delete"
                                data-tracking-id="delete-automation-edit-actions"
                                LeadingIcon={IconTrashCan}
                            >
                                Delete
                            </DropdownMenuItem>
                        </>
                    )}
                </DropdownActions>
            )}
            <Button variant="secondary" onClick={onCancel} data-tracking-id="cancel">
                Cancel
            </Button>
            {isTemplate ? (
                <Button
                    variant="primary"
                    onClick={onCreate}
                    data-tracking-id="create-automation-from-template"
                    loading={isCreatingWorkflow}
                >
                    Create
                </Button>
            ) : workflow ? (
                <Button
                    variant="primary"
                    data-tracking-id="save-automation-steps-automation-edit-page"
                    loading={isUpdatingWorkflow}
                    disabled={!isDirty}
                    onClick={() => onSave(workflow.id)}
                    LeadingIcon={IconFloppyDisk}
                >
                    Save
                </Button>
            ) : null}
        </div>
    );
};
